import type { Location } from "history";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { IasAppShell } from "ias/deployment-adapter/AppShell";
import { AppBarContainer } from "metabase/app/nav/AppBar";
import { Navbar } from "metabase/app/nav/Navbar";
import {
  getIsAdminApp,
  getIsAppBarVisible,
  getIsDataStudioApp,
  getIsNavBarEnabled,
} from "metabase/app/selectors";
import { AppBanner } from "metabase/common/components/AppBanner";
import {
  Archived,
  GenericError,
  KeyboardTriggeredErrorModal,
  NotFound,
  Unauthorized,
} from "metabase/common/components/ErrorPages";
import { UndoListing } from "metabase/common/components/UndoListing";
import { ContentViewportContext } from "metabase/common/context/ContentViewportContext";
import CS from "metabase/css/core/index.css";
import ScrollToTop from "metabase/hoc/ScrollToTop";
import { usePageTitle } from "metabase/hooks/use-page-title";
import { connect, useSelector } from "metabase/redux";
import { setErrorPage } from "metabase/redux/app";
import type { AppErrorDescriptor, State } from "metabase/redux/store";
import { getErrorPage } from "metabase/selectors/app";
import { getApplicationName } from "metabase/selectors/whitelabel";
import { StatusListing } from "metabase/status/components/StatusListing";
import { initializeIframeResizer } from "metabase/utils/dom";

import { AppContainer, AppContent, AppContentContainer } from "./App.styled";
import { AppKBarProvider } from "./AppKBarProvider";
import ErrorBoundary from "./ErrorBoundary";
import { useTokenRefresh } from "./api/utils/use-token-refresh";
import { Metabot } from "./metabot/components/Metabot";
import { NewModals } from "./new/components/NewModals/NewModals";
import { Palette } from "./palette/components/Palette";

const getErrorComponent = ({ status, data, context }: AppErrorDescriptor) => {
  if (status === 403 || data?.error_code === "unauthorized") {
    return <Unauthorized />;
  }
  if (status === 404 || data?.error_code === "not-found") {
    return <NotFound />;
  }
  if (data?.error_code === "archived" && context === "dashboard") {
    return <Archived entityName="dashboard" linkTo="/dashboards/archive" />;
  }
  if (data?.error_code === "archived" && context === "query-builder") {
    return <Archived entityName="question" linkTo="/questions/archive" />;
  }
  return <GenericError details={data?.message} />;
};

interface AppStateProps {
  errorPage: AppErrorDescriptor | null;
  isAdminApp: boolean;
  isDataStudioApp: boolean;
  bannerMessageDescriptor?: string;
  isAppBarVisible: boolean;
  isNavBarEnabled: boolean;
}

interface AppDispatchProps {
  onError: (error: unknown) => void;
}

interface AppRouterOwnProps {
  location: Location;
  children: ReactNode;
}

type AppProps = AppStateProps & AppDispatchProps & AppRouterOwnProps;

const mapStateToProps = (
  state: State,
  props: AppRouterOwnProps,
): AppStateProps => ({
  errorPage: getErrorPage(state),
  isAdminApp: getIsAdminApp(state, props),
  isDataStudioApp: getIsDataStudioApp(state, props),
  isAppBarVisible: getIsAppBarVisible(state, props),
  isNavBarEnabled: getIsNavBarEnabled(state, props),
});

const mapDispatchToProps: AppDispatchProps = {
  onError: setErrorPage,
};

function App({
  errorPage,
  isAdminApp,
  isDataStudioApp,
  isAppBarVisible,
  isNavBarEnabled,
  children,
  onError,
}: AppProps) {
  const [viewportElement, setViewportElement] = useState<HTMLElement | null>();
  const applicationName = useSelector(getApplicationName);

  usePageTitle(applicationName, { titleIndex: 0 });
  // IAS overlay: `useTokenRefresh` used to live here, but its
  // `useGetSettingsQuery` subscription fires a `/api/session/properties`
  // request as soon as this component mounts — which is BEFORE
  // `AppSettingsGetter` (inside `IasAppShell`) points the API client at the
  // real backend origin, so the request races and hits the CDN. Rendered
  // inside the shell instead via `<TokenRefreshEffect />` below.

  useEffect(() => {
    initializeIframeResizer();
  }, []);

  return (
    <IasAppShell>
      <TokenRefreshEffect />
      <ErrorBoundary onError={onError}>
        <ScrollToTop>
          <AppKBarProvider>
            <KeyboardTriggeredErrorModal />
            <AppContainer className={CS.spread}>
              <AppBanner />
              {isAppBarVisible && <AppBarContainer />}
              <AppContentContainer isAdminApp={isAdminApp}>
                {isNavBarEnabled && <Navbar />}
                <AppContent ref={setViewportElement}>
                  <ContentViewportContext.Provider
                    value={viewportElement ?? null}
                  >
                    {errorPage ? getErrorComponent(errorPage) : children}
                  </ContentViewportContext.Provider>
                </AppContent>
                <UndoListing />
                <StatusListing />
                <NewModals />
                <Metabot hide={isAdminApp || isDataStudioApp} />
              </AppContentContainer>
            </AppContainer>
            <Palette />
          </AppKBarProvider>
        </ScrollToTop>
      </ErrorBoundary>
    </IasAppShell>
  );
}

// IAS overlay: side-effect-only child of `IasAppShell` so `useTokenRefresh` —
// and the `useGetSettingsQuery` subscription it opens — waits until the shell
// has finished the startup gate and the API client is pointed at the backend.
function TokenRefreshEffect() {
  useTokenRefresh();
  return null;
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default connect<AppStateProps, unknown, AppRouterOwnProps, State>(
  mapStateToProps,
  mapDispatchToProps,
)(App);
